import { supabase, supabaseAdmin } from '../supabase';

// Reports
export const sendReportToSupabase = async (reportData: any) => {
    const { data, error } = await supabase
        .from('reports')
        .insert({
            report_data: reportData,
            location: reportData.location,
            date: reportData.date,
            machine: reportData.machine,
            status: reportData.status,
            type: reportData.type
        });

    if (error) throw error;
    return data;
};

export const getReports = async () => {
    const { data, error } = await supabase
        .from('reports')
        .select('*')
        .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
};

export const getReportsByLocation = async (location: string) => {
    const { data, error } = await supabase
        .from('reports')
        .select('*')
        .eq('location', location)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
};

// Locations
export const getLocations = async () => {
    const { data, error } = await supabase
        .from('locations')
        .select('*')
        .order('name');

    if (error) throw error;
    return data.map(location => location.name);
};

// Technicians/Assignees
export const getTechnicians = async () => {
    const { data, error } = await supabase
        .from('technicians')
        .select('*')
        .order('name');

    if (error) {
        console.error("Error fetching technicians:", error);
        // Fallback for when the table doesn't exist yet
        if (error.code === '42P01') {
            return ['Alex Bilodeau', 'Jegan Thiruchelvam', 'Chirag Sharma', 'Pierre Paquette', 'Jeff Jackson'];
        }
        throw error;
    }
    return data.map(tech => tech.name);
};

export const addTechnician = async (name: string) => {
    const { data, error } = await supabase
        .from('technicians')
        .insert({ name });

    if (error) throw error;
    return data;
};

// Machines
export const getMachinesByLocation = async (location: string) => {
    const { data, error } = await supabase
        .from('machines')
        .select('*')
        .eq('location', location);

    if (error) throw error;
    return data;
};

// Get all machines
export const getMachines = async () => {
    const { data, error } = await supabase
        .from('machines')
        .select('*');

    if (error) throw error;
    return data;
};

// Uptime Percentages
export const getOverallUptime = async (year: number) => {
    const { data, error } = await supabase
        .from('uptime_percentages')
        .select('percentage')
        .eq('year', year)
        .eq('location', 'all')
        .eq('division', 'overall')
        .single();

    if (error) throw error;
    return data?.percentage || 0;
};

export const getLocationUptime = async (year: number) => {
    // First try to get all uptime percentages
    const { data, error } = await supabase
        .from('uptime_percentages')
        .select('location, division, percentage')
        .eq('year', year)
        .or('division.eq.All,division.eq.all,division.ilike.all'); // Case-insensitive match for 'all'

    if (error) throw error;



    // Format data similar to Firebase structure
    const performances = data
        .filter(item => item && item.location && item.percentage !== null && item.percentage !== undefined)
        .map(item => ({
            location: item.location,
            division: item.division,
            percentage: parseFloat(item.percentage)
        }))
        // Filter out the 'all' location and any invalid entries
        .filter(item =>
            item.location &&
            item.location.toLowerCase() !== 'all' &&
            !isNaN(item.percentage)
        );



    // Create a copy of the array for best (sorted descending)
    const bestPerformances = [...performances].sort((a, b) => b.percentage - a.percentage);

    // Create a copy of the array for worst (sorted ascending)
    // Filter out locations with 100% uptime for worst performers
    const nonPerfectLocations = performances.filter(item => item.percentage < 100);



    // If we have locations with less than 100% uptime, use those
    // Otherwise, fall back to the lowest performing locations even if they're at 100%
    const worstLocationsPool = nonPerfectLocations.length > 0 ? nonPerfectLocations : performances;
    const worstPerformances = [...worstLocationsPool].sort((a, b) => a.percentage - b.percentage);



    return {
        best: bestPerformances.slice(0, 5),
        worst: worstPerformances.slice(0, 5)
    };
};

// Update uptime percentage for a location and division
export const updateUptimePercentage = async (year: number, location: string, division: string, percentage: number) => {
    const { data, error } = await supabase
        .from('uptime_percentages')
        .upsert({
            year,
            location,
            division,
            percentage
        }, { onConflict: 'year,location,division' });

    if (error) throw error;
    return data;
};

// Get uptime percentages for all locations
export const getUptimePercentagesByLocation = async (year: number, division: string = 'All') => {
    const { data, error } = await supabase
        .from('uptime_percentages')
        .select('location, percentage')
        .eq('year', year)
        .eq('division', division);

    if (error) throw error;

    // Convert to object format similar to Firebase
    const uptimeByLocation: Record<string, number> = {};
    data.forEach(item => {
        uptimeByLocation[item.location] = item.percentage;
    });

    return uptimeByLocation;
};

// Schedule
export const getSchedule = async () => {
    const { data, error } = await supabase
        .from('schedule')
        .select('*')
        .order('start', { ascending: true });

    if (error) throw error;
    return data;
};

export const getCompletedSchedulesByQuarter = async (year: number, quarter: number) => {
    // Calculate quarter date range
    const startMonth = (quarter - 1) * 3;
    const startDate = new Date(year, startMonth, 1);
    const endDate = new Date(year, startMonth + 3, 0);

    const { data, error } = await supabase
        .from('schedule')
        .select('*')
        .eq('status', 'Completed')
        .gte('start', startDate.toISOString())
        .lte('end', endDate.toISOString());

    if (error) throw error;
    return data;
};

// Storage
export const uploadFile = async (bucket: string, path: string, file: File) => {
    const { data, error } = await supabase.storage
        .from(bucket)
        .upload(path, file, {
            cacheControl: '3600',
            upsert: true
        });

    if (error) throw error;
    return data;
};

export const getFileUrl = async (bucket: string, path: string) => {
    const { data } = await supabase.storage
        .from(bucket)
        .getPublicUrl(path);

    return data.publicUrl;
};

// Helper function to convert Firebase-style data to Supabase format
export const convertFirebaseToSupabaseFormat = (firebaseData: any) => {
    if (!firebaseData) return [];

    // If it's an object with keys, convert to array with IDs
    if (typeof firebaseData === 'object' && !Array.isArray(firebaseData)) {
        return Object.entries(firebaseData).map(([id, data]) => ({
            id,
            ...(data as object)
        }));
    }

    // If it's already an array, return as is
    return firebaseData;
};

// Add a new report
export const addReport = async (reportData: any) => {
    // Generate a UUID for the report if it doesn't have one
    const reportWithId = {
        ...reportData,
        id: reportData.id || crypto.randomUUID() // Use crypto.randomUUID() to generate a UUID
    };

    const { data, error } = await supabase
        .from('reports')
        .insert({
            report_data: reportWithId,
            location: reportData.location,
            date: reportData.date,
            machine: reportData.machineId,
            status: reportData.status || 'Down',
            type: reportData.cause
        });

    if (error) throw error;
    return data;
};

// Update an existing report
export const updateReport = async (reportId: string, reportData: any) => {
    const { data, error } = await supabase
        .from('reports')
        .update({
            report_data: reportData,
            location: reportData.location,
            date: reportData.date,
            machine: reportData.machineId,
            status: reportData.status || 'Down',
            type: reportData.cause
        })
        .eq('id', reportId);

    if (error) throw error;
    return data;
};

// Delete a report
export const deleteReport = async (reportId: string) => {
    const { data, error } = await supabase
        .from('reports')
        .delete()
        .eq('id', reportId);

    if (error) throw error;
    return data;
};

// Add a new location
export const addLocation = async (locationName: string) => {
    const { data, error } = await supabase
        .from('locations')
        .insert({
            name: locationName
        });

    if (error) throw error;
    return data;
};

// Schedule events
export const addScheduleEvent = async (eventData: any) => {
    // Ensure we're only using the date part (YYYY-MM-DD)
    const startDate = eventData.start;
    const endDate = eventData.end_date || eventData.start;

    try {
        const { data, error } = await supabase
            .from('schedule')
            .insert({
                title: eventData.title,
                start: startDate,
                end_date: endDate,
                location: eventData.location,
                assignee: eventData.assignee,
                status: eventData.status,
                note: eventData.note || ''
            })
            .select(); // Add select() to return the inserted data

        if (error) throw error;

        // If data is null or empty, try to fetch the most recently added event
        if (!data || data.length === 0) {

            const { data: latestData, error: latestError } = await supabase
                .from('schedule')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(1);

            if (latestError) throw latestError;
            return latestData;
        }

        return data;
    } catch (error) {
        console.error("Error in addScheduleEvent:", error);
        throw error;
    }
};

export const updateScheduleEvent = async (eventId: string, eventData: any) => {
    // Ensure we're only using the date part (YYYY-MM-DD)
    const startDate = eventData.start;
    const endDate = eventData.end_date || eventData.start;

    try {
        const { data, error } = await supabase
            .from('schedule')
            .update({
                title: eventData.title,
                start: startDate,
                end_date: endDate,
                location: eventData.location,
                assignee: eventData.assignee,
                status: eventData.status,
                note: eventData.note || ''
            })
            .eq('id', eventId)
            .select(); // Add select() to return the updated data

        if (error) throw error;

        // If data is null or empty, try to fetch the updated event
        if (!data || data.length === 0) {

            const { data: updatedData, error: fetchError } = await supabase
                .from('schedule')
                .select('*')
                .eq('id', eventId)
                .single();

            if (fetchError) throw fetchError;
            return [updatedData]; // Return in array format for consistency
        }

        return data;
    } catch (error) {
        console.error("Error in updateScheduleEvent:", error);
        throw error;
    }
};

export const deleteScheduleEvent = async (eventId: string) => {
    const { data, error } = await supabase
        .from('schedule')
        .delete()
        .eq('id', eventId);

    if (error) throw error;
    return data;
};

// Inspection reports
export const saveInspectionReport = async (reportData: any) => {
    try {
        // Try to insert the data
        const { data, error } = await supabaseAdmin
            .from('inspection_reports')
            .insert({
                location: reportData.location,
                machine: reportData.machine,
                machine_name: reportData.machineName || reportData.machine,
                date: reportData.date,
                technician: reportData.technician,
                checklist: reportData.checklist,
                pdf_url: reportData.pdfUrl || null
            });

        if (error) {
            console.error('Error saving inspection report to Supabase:', error);

            // Check if the error is because the table doesn't exist
            if (error.code === '42P01' || (error.message && error.message.includes('relation "inspection_reports" does not exist'))) {
                throw new Error('The inspection_reports table does not exist. Please create it in your Supabase SQL Editor.');
            }

            throw error;
        }


        return data;
    } catch (error) {
        console.error('Error in saveInspectionReport:', error);
        throw error;
    }
};

// Check if a bucket exists and create it if it doesn't
export const ensureBucketExists = async (bucketName: string) => {
    try {
        // Use the exact bucket name without formatting
        // Use the exact bucket name without formatting

        // List all buckets
        const { data: buckets, error: listError } = await supabaseAdmin
            .storage
            .listBuckets();

        if (listError) {
            console.error('Error listing buckets:', listError);
            throw listError;
        }

        // Check if our bucket exists
        const bucketExists = buckets.some(bucket => bucket.name === bucketName);

        if (!bucketExists) {
            throw new Error(`Bucket ${bucketName} does not exist. Please create it in the Supabase dashboard.`);
            throw new Error(`Bucket ${bucketName} does not exist. Please create it in the Supabase dashboard.`);
        } else {
            return true;
            return true;
        }
    } catch (error) {
        console.error('Error in ensureBucketExists:', error);
        throw error;
    }
};

// Upload file to Supabase storage
export const uploadFileToStorage = async (bucket: string, filePath: string, fileBlob: Blob) => {
    try {


        // Convert Blob to File if needed
        let fileToUpload = fileBlob;
        if (!(fileBlob instanceof File)) {
            // Create a File object from the Blob
            const fileName = filePath.split('/').pop() || 'file';
            fileToUpload = new File([fileBlob], fileName, {
                type: fileBlob.type || 'application/octet-stream'
            });
        }

        // First try with regular supabase client since storage policies have been updated
        try {
            const { data, error } = await supabase
                .storage
                .from(bucket)
                .upload(filePath, fileToUpload, {
                    cacheControl: '3600',
                    upsert: true
                });

            if (error) {

                throw error; // This will be caught by the outer try/catch
            }



            // Get the public URL
            const { data: urlData } = await supabase
                .storage
                .from(bucket)
                .getPublicUrl(filePath);

            return urlData.publicUrl;
        } catch (regularClientError) {
            // Fall back to admin client if regular client fails
            const { data, error } = await supabaseAdmin
                .storage
                .from(bucket)
                .upload(filePath, fileToUpload, {
                    cacheControl: '3600',
                    upsert: true
                });

            if (error) {
                console.error('Admin client upload failed:', error);
                throw error;
            }



            // Get the public URL
            const { data: urlData } = await supabaseAdmin
                .storage
                .from(bucket)
                .getPublicUrl(filePath);

            return urlData.publicUrl;
        }
    } catch (error) {
        console.error('Error in uploadFileToStorage:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        throw error;
    }
};

// Delete file from Supabase storage
export const deleteFileFromStorage = async (bucket: string, filePath: string) => {
    try {


        // If the filePath is a full URL, extract just the path part
        if (filePath.startsWith('http')) {
            try {
                const url = new URL(filePath);
                const segments = url.pathname.split('/');

                // Supabase storage URL format: /storage/v1/object/[public|authenticated|sign]/[bucket]/[path]
                const objectIndex = segments.indexOf('object');
                if (objectIndex !== -1 && segments.length > objectIndex + 3) {
                    filePath = segments.slice(objectIndex + 3).join('/');
                } else {
                    // Fallback: extract everything after the bucket name
                    const bucketIndex = segments.indexOf(bucket);
                    if (bucketIndex !== -1) {
                        filePath = segments.slice(bucketIndex + 1).join('/');
                    } else {
                        // Case-insensitive bucket search
                        const lowerBucketIndex = segments.findIndex(s => s.toLowerCase() === bucket.toLowerCase());
                        if (lowerBucketIndex !== -1) {
                            filePath = segments.slice(lowerBucketIndex + 1).join('/');
                        }
                    }
                }

                filePath = decodeURIComponent(filePath);
            } catch (urlError) {
                console.error('Error parsing URL in deleteFileFromStorage:', urlError);
            }
        }

        // First try with regular supabase client since storage policies have been updated
        try {
            const { data, error } = await supabase
                .storage
                .from(bucket)
                .remove([filePath]);

            if (error) {

                throw error; // This will be caught by the outer try/catch
            }


            return data;
        } catch (regularClientError) {


            // Fall back to admin client if regular client fails
            const { data, error } = await supabaseAdmin
                .storage
                .from(bucket)
                .remove([filePath]);

            if (error) {
                console.error('Admin client deletion failed:', error);
                console.error('Error details:', JSON.stringify(error, null, 2));

                // Try one more approach - if the path doesn't start with 'private/', add it
                if (!filePath.startsWith('private/')) {
                    const newPath = `private/${filePath.split('/').pop()}`;

                    try {
                        const { data: retryData, error: retryError } = await supabaseAdmin
                            .storage
                            .from(bucket)
                            .remove([newPath]);

                        if (!retryError) {
                            return retryData;
                        } else {
                            console.error('Final deletion attempt failed:', retryError);
                        }
                    } catch (finalError) {
                        console.error('Final deletion attempt threw exception:', finalError);
                    }
                }

                // Don't throw, just return null
                return null;
            }


            return data;
        }
    } catch (error) {
        console.error('Error in deleteFileFromStorage:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        // Don't throw the error, just log it and return null
        return null;
    }
};

export const getInspectionReports = async () => {
    try {
        // Use the admin client with service role key to bypass RLS
        const { data, error } = await supabaseAdmin
            .from('inspection_reports')
            .select('*')
            .order('date', { ascending: false });

        if (error) {
            console.error('Error fetching inspection reports from Supabase:', error);
            throw error;
        }

        return data;
    } catch (error) {
        console.error('Error in getInspectionReports:', error);
        throw error;
    }
};

export const deleteInspectionReport = async (reportId: string) => {
    try {
        // Use the admin client with service role key to bypass RLS
        const { data, error } = await supabaseAdmin
            .from('inspection_reports')
            .delete()
            .eq('id', reportId);

        if (error) {
            console.error('Error deleting inspection report from Supabase:', error);
            throw error;
        }

        return data;
    } catch (error) {
        console.error('Error in deleteInspectionReport:', error);
        throw error;
    }
};

// Get a fresh download URL for a file
export const getDownloadURL = async (bucket: string, filePath: string) => {
    try {
        // If the filePath is a full URL, extract just the path part
        if (filePath.startsWith('http')) {
            try {
                const url = new URL(filePath);
                const segments = url.pathname.split('/');

                // Supabase storage URL format: /storage/v1/object/[public|authenticated|sign]/[bucket]/[path]
                const objectIndex = segments.indexOf('object');
                if (objectIndex !== -1 && segments.length > objectIndex + 3) {
                    filePath = segments.slice(objectIndex + 3).join('/');
                } else {
                    // Fallback: extract everything after the bucket name
                    const bucketIndex = segments.indexOf(bucket);
                    if (bucketIndex !== -1) {
                        filePath = segments.slice(bucketIndex + 1).join('/');
                    } else {
                        // Case-insensitive bucket search
                        const lowerBucketIndex = segments.findIndex(s => s.toLowerCase() === bucket.toLowerCase());
                        if (lowerBucketIndex !== -1) {
                            filePath = segments.slice(lowerBucketIndex + 1).join('/');
                        }
                    }
                }

                filePath = decodeURIComponent(filePath);
            } catch (urlError) {
                // Silent error - continue with original filePath
            }
        }

        // Try with regular client first
        try {
            const { data, error } = await supabase
                .storage
                .from(bucket)
                .createSignedUrl(filePath, 60); // 60 seconds expiry

            if (error) {
                throw error;
            }

            return data.signedUrl;
        } catch (regularClientError) {
            // Try with admin client
            try {
                const { data, error } = await supabaseAdmin
                    .storage
                    .from(bucket)
                    .createSignedUrl(filePath, 60); // 60 seconds expiry

                if (error) {
                    throw error;
                }

                return data.signedUrl;
            } catch (adminClientError) {
                // Admin client failed, try public URL as fallback

                // Last resort: try to get a public URL
                const { data } = await supabaseAdmin
                    .storage
                    .from(bucket)
                    .getPublicUrl(filePath);

                return data.publicUrl;
            }
        }
    } catch (error) {
        console.error('Error in getDownloadURL:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        throw error;
    }
};

// Function to update all reports to ensure end_date is at the top level
export const updateAllReportsEndDate = async () => {
    try {
        // First, get all reports
        const { data: reports, error: fetchError } = await supabase
            .from('reports')
            .select('*');

        if (fetchError) throw fetchError;



        let updatedCount = 0;

        // Process each report
        for (const report of reports) {
            // Check if report has report_data with endDate but no top-level end_date
            if (report.report_data?.endDate && !report.end_date) {


                // Update the report to add end_date at the top level
                const { error: updateError } = await supabase
                    .from('reports')
                    .update({ end_date: report.report_data.endDate })
                    .eq('id', report.id);

                if (updateError) {
                    console.error(`Error updating report ${report.id}:`, updateError);
                } else {
                    updatedCount++;
                }
            }
        }


        return { updatedCount, totalReports: reports.length };
    } catch (error) {
        console.error('Error updating reports end_date:', error);
        throw error;
    }
}; 